import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Sparkle } from "@phosphor-icons/react";

/**
 * DeletePropertyReasonModal — chiede il motivo della cancellazione
 * (venduto, ritirato, scaduto, duplicato, altro) per popolare il report
 * annuale "Pronto Wrapped".
 *
 * Quando "venduto" → mostra anche campo prezzo finale + data rogito.
 * L'utente PUÒ saltare (clicca "Elimina senza specificare") e il delete
 * avviene comunque (backwards compatible).
 */
const REASONS = [
  { v: "sold",      l: "Venduto",          desc: "Rogito firmato · andrà nel tuo Wrapped annuale", primary: true },
  { v: "withdrawn", l: "Ritirato",         desc: "Il proprietario ha ritirato l'incarico" },
  { v: "expired",   l: "Mandato scaduto",  desc: "Il mandato in esclusiva è terminato" },
  { v: "duplicate", l: "Duplicato",        desc: "Era una bozza o un doppio" },
  { v: "other",     l: "Altro",            desc: "Altro motivo non in lista" },
];

export default function DeletePropertyReasonModal({ open, title, askingPrice, onClose, onConfirm }) {
  const [reason, setReason] = useState(null);
  const [finalPrice, setFinalPrice] = useState("");
  const [closedAt, setClosedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (skipReason = false) => {
    setBusy(true);
    try {
      const payload = skipReason ? {} : {
        reason,
        final_price_eur: reason === "sold" ? parseFloat(String(finalPrice).replace(/[^\d.,]/g, "").replace(",", ".")) || null : null,
        closed_at: reason === "sold" && closedAt ? new Date(closedAt + "T12:00:00").toISOString() : null,
        note: note.trim() || null,
      };
      await onConfirm(payload);
    } finally {
      setBusy(false);
    }
  };

  // iter 313 r2 — il modal viene renderizzato dentro <PropertyCardActions>
  // che è figlio del <Link> della card immobile. Senza Portal qualsiasi click
  // sul backdrop o sui bottoni bolla fino al <Link> → navigation indesiderata
  // verso /property/:id invece di eliminare. createPortal lo sposta in
  // document.body, fuori dall'albero del Link.
  return createPortal(
    <div
      // iter 309 r15: fix mobile bottom-sheet che tagliava il bottone "Elimina".
      className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={(e) => {
        // iter 313 r2 — blocca SEMPRE la propagazione anche su backdrop click,
        // così se per qualunque ragione il modal vive ancora dentro <Link>
        // (compatibilità futura) il click non naviga.
        e.preventDefault();
        e.stopPropagation();
        if (!busy) onClose();
      }}
      data-testid="delete-reason-modal"
      style={{ paddingTop: "max(12px, env(safe-area-inset-top))", paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[460px] p-5 sm:p-6 shadow-2xl my-auto"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C24E4E] mb-1">
              Elimini annuncio
            </div>
            <h2 className="text-[17px] font-semibold text-[#1C1C1A] leading-tight">
              Come mai elimini «{title || "questo annuncio"}»?
            </h2>
            <p className="text-[11.5px] text-[#6B6A65] mt-1.5 leading-snug">
              Aiuta Pronto a costruire il tuo <strong className="text-[#7C8B6E]">report annuale</strong> shareable.
              Lo puoi anche saltare.
            </p>
          </div>
          <button
            onClick={() => !busy && onClose()}
            className="text-[#9A968B] hover:text-[#1C1C1A] p-1"
            aria-label="Chiudi"
            data-testid="delete-reason-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Reason picker */}
        <div className="space-y-1.5 mb-3" data-testid="delete-reason-options">
          {REASONS.map((r) => (
            <button
              key={r.v}
              onClick={() => setReason(r.v)}
              className={`w-full text-left px-3.5 py-2.5 rounded-lg border transition-all ${
                reason === r.v
                  ? "border-[#7C8B6E] bg-[#F1F4ED]"
                  : "border-[#EAE7DF] bg-white hover:border-[#7C8B6E]"
              }`}
              data-testid={`delete-reason-${r.v}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold text-[#1C1C1A] inline-flex items-center gap-1.5">
                  {r.primary && <Sparkle size={11} weight="fill" className="text-[#7C8B6E]" />}
                  {r.l}
                </div>
                <div className={`w-3.5 h-3.5 rounded-full border-2 ${reason === r.v ? "border-[#7C8B6E] bg-[#7C8B6E]" : "border-[#9A968B]"}`} />
              </div>
              <div className="text-[11px] text-[#6B6A65] mt-0.5">{r.desc}</div>
            </button>
          ))}
        </div>

        {/* Extra fields if SOLD */}
        {reason === "sold" && (
          <div className="bg-[#F1F4ED] rounded-lg p-3 mb-3 space-y-3" data-testid="delete-reason-sold-fields">
            <div>
              <label className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] mb-1 block">
                Prezzo finale di vendita {askingPrice ? <span className="text-[#9A968B] normal-case tracking-normal font-normal">· chiesto €{Number(askingPrice).toLocaleString("it-IT")}</span> : null}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={finalPrice}
                onChange={(e) => setFinalPrice(e.target.value)}
                placeholder="es. 295000"
                className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="delete-reason-final-price"
              />
            </div>
            <div>
              <label className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#6B6A65] mb-1 block">
                Data rogito
              </label>
              <input
                type="date"
                value={closedAt}
                onChange={(e) => setClosedAt(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="delete-reason-closed-at"
              />
            </div>
          </div>
        )}

        {/* Optional note */}
        {reason && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (opzionale)"
            rows={2}
            maxLength={500}
            className="w-full px-3 py-2 bg-white border border-[#E5E7E0] rounded text-[13px] focus:outline-none focus:border-[#7C8B6E] mb-3 resize-none"
            data-testid="delete-reason-note"
          />
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 mt-2 pt-2 sticky bottom-0 bg-white">
          <button
            onClick={() => submit(true)}
            disabled={busy}
            className="text-[12px] text-[#9A968B] hover:text-[#1C1C1A] py-2.5 px-3 disabled:opacity-50"
            data-testid="delete-reason-skip"
          >
            Elimina senza specificare
          </button>
          <button
            onClick={() => submit(false)}
            disabled={busy || !reason}
            className="flex-1 py-3 sm:py-2.5 rounded-full bg-[#C24E4E] hover:bg-[#A33D3D] text-white text-[14px] sm:text-[13px] font-semibold disabled:opacity-50 transition-colors"
            data-testid="delete-reason-confirm"
          >
            {busy ? "Elimino…" : reason === "sold" ? "Registra vendita ed elimina" : "Elimina"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
